// ========================================
// CONFIGURAÇÃO GLOBAL DE STATUS
// src/config/statusConfig.js
// ========================================
// Este arquivo centraliza todas as configurações de cores, ícones e labels
// para os status de pedidos/orçamentos. Importe em qualquer componente para usar.
//
// FLUXO DO SISTEMA:
// 1. Orçamento criado -> status: AGUARDANDO APROVAÇÃO
// 2. Aprovação do orçamento:
//    - ORÇAMENTO APROVADO -> Responsável pode "Fazer Pedido"
//    - SOLICITAR EDIÇÃO -> Volta para responsável editar e reenviar
//    - APROVADO COM AJUSTES -> Aprovado com alterações feitas na aprovação
//    - RECUSADO -> Orçamento recusado
// 3. Após "Fazer Pedido" -> status: EM PROCESSAMENTO
// 4. Finalização (Admin/Diretoria/Administrativo):
//    - PEDIDO FATURADO
//    - PEDIDO CANCELADO
//
// COMO USAR:
// 1. Crie a pasta: src/config/
// 2. Coloque este arquivo em: src/config/statusConfig.js
// 3. Importe nos componentes:
//    import { getStatusConfig, getStatusByScore, STATUS_CONFIG, FASE_CONFIG } from './config/statusConfig';

import { 
  XCircle, 
  AlertTriangle, 
  ArrowRight, 
  CheckCircle, 
  Trophy,
  Check,
  X,
  RotateCcw,
  Clock,
  Edit3,
  FileCheck,
  FileX,
  Package,
  PackageCheck,
  PackageX,
  Send,
  ClipboardCheck
} from 'lucide-react';

// ========================================
// CONFIGURAÇÃO DAS FASES DO FLUXO
// ========================================
export const FASE_CONFIG = {
  'AGUARDANDO_PROXIMA_ETAPA': {
    label: 'Aguardando Próxima Etapa',
    labelShort: 'aguardando',
    bgGradient: 'from-amber-400 to-amber-500',
    bgSolid: 'bg-amber-400',
    bgLight: 'bg-amber-50',
    textColor: 'text-white',
    textColorDark: 'text-amber-700',
    borderColor: 'border-amber-300',
    icon: Clock,
    iconEmoji: '⏳',
    description: 'Orçamento aguardando próxima etapa',
    ordem: 1
  },
  'ORCAMENTO_APROVADO': {
    label: 'Orçamento Aprovado',
    labelShort: 'aprovado',
    bgGradient: 'from-emerald-500 to-emerald-600',
    bgSolid: 'bg-emerald-500',
    bgLight: 'bg-emerald-50',
    textColor: 'text-white',
    textColorDark: 'text-emerald-700',
    borderColor: 'border-emerald-300',
    icon: FileCheck,
    iconEmoji: '✓',
    description: 'Orçamento aprovado, aguardando confirmação do pedido',
    ordem: 2
  },
  'ORCAMENTO_APROVADO_AUTOMATICAMENTE': {
    label: 'Aprovado Automaticamente',
    labelShort: 'Aprov. Auto',
    bgGradient: 'from-teal-500 to-cyan-500',
    bgSolid: 'bg-teal-500',
    bgLight: 'bg-teal-50',
    textColor: 'text-white',
    textColorDark: 'text-teal-700',
    borderColor: 'border-teal-300',
    icon: FileCheck,
    iconEmoji: '✓',
    description: 'Orçamento aprovado automaticamente (fluxo automático)',
    ordem: 2
  },
  'SOLICITAR_EDICAO': {
    label: 'Solicitada Edição',
    labelShort: 'edição solicitada',
    bgGradient: 'from-orange-400 to-orange-500',
    bgSolid: 'bg-orange-400',
    bgLight: 'bg-orange-50',
    textColor: 'text-white',
    textColorDark: 'text-orange-700',
    borderColor: 'border-orange-300',
    icon: Edit3,
    iconEmoji: '✏️',
    description: 'Orçamento devolvido para edição pelo responsável',
    ordem: 2
  },
  'APROVADO_COM_AJUSTES': {
    label: 'Aprovado com Ajustes',
    labelShort: 'com ajustes',
    bgGradient: 'from-blue-500 to-sky-500',
    bgSolid: 'bg-blue-500',
    bgLight: 'bg-blue-50',
    textColor: 'text-white',
    textColorDark: 'text-blue-700',
    borderColor: 'border-blue-300',
    icon: ClipboardCheck,
    iconEmoji: '📋✓',
    description: 'Orçamento aprovado com ajustes feitos pela aprovação',
    ordem: 2
  },
  'ORCAMENTO_RECUSADO': {
    label: 'Orçamento Recusado',
    labelShort: 'recusado',
    bgGradient: 'from-red-500 to-red-600',
    bgSolid: 'bg-red-500',
    bgLight: 'bg-red-50',
    textColor: 'text-white',
    textColorDark: 'text-red-700',
    borderColor: 'border-red-300',
    icon: FileX,
    iconEmoji: '✗',
    description: 'Orçamento recusado pela aprovação',
    ordem: 2
  },
  'EM_PROCESSAMENTO': {
    label: 'Em Processamento',
    labelShort: 'processando',
    bgGradient: 'from-blue-500 to-blue-600',
    bgSolid: 'bg-blue-500',
    bgLight: 'bg-blue-50',
    textColor: 'text-white',
    textColorDark: 'text-blue-700',
    borderColor: 'border-blue-300',
    icon: Package,
    iconEmoji: '📦',
    description: 'Pedido em processamento',
    ordem: 3
  },
  'PEDIDO_FATURADO': {
    label: 'Pedido Faturado',
    labelShort: 'faturado',
    bgGradient: 'from-green-600 to-green-700',
    bgSolid: 'bg-green-600',
    bgLight: 'bg-green-50',
    textColor: 'text-white',
    textColorDark: 'text-green-700',
    borderColor: 'border-green-300',
    icon: PackageCheck,
    iconEmoji: '✅',
    description: 'Pedido faturado com sucesso',
    ordem: 4
  },
  'PEDIDO_CANCELADO': {
    label: 'Pedido Cancelado',
    labelShort: 'cancelado',
    bgGradient: 'from-gray-500 to-gray-600',
    bgSolid: 'bg-gray-500',
    bgLight: 'bg-gray-50',
    textColor: 'text-white',
    textColorDark: 'text-gray-700',
    borderColor: 'border-gray-300',
    icon: PackageX,
    iconEmoji: '❌',
    description: 'Pedido cancelado',
    ordem: 4
  },
  'PEDIDO_CANCELADO_POS_FATURAMENTO': {
    label: 'Cancelado Após Faturamento',
    labelShort: 'canc. pós-fat.',
    bgGradient: 'from-rose-600 to-red-700',
    bgSolid: 'bg-rose-600',
    bgLight: 'bg-rose-50',
    textColor: 'text-white',
    textColorDark: 'text-rose-700',
    borderColor: 'border-rose-300',
    icon: PackageX,
    iconEmoji: '❌',
    description: 'Pedido cancelado após ter sido faturado',
    ordem: 5
  },
  'ORCAMENTO_CANCELADO': {
    label: 'Orçamento Cancelado',
    labelShort: 'cancelado',
    bgGradient: 'from-red-500 to-rose-600',
    bgSolid: 'bg-red-500',
    bgLight: 'bg-red-50',
    textColor: 'text-white',
    textColorDark: 'text-red-700',
    borderColor: 'border-red-300',
    icon: FileX,
    iconEmoji: '✗',
    description: 'Orçamento cancelado pelo responsável',
    ordem: 5
  },
  'PEDIDO_CONFIRMADO': {
    label: 'Pedido Confirmado',
    labelShort: 'confirmado',
    bgGradient: 'from-blue-600 to-blue-700',
    bgSolid: 'bg-blue-600',
    bgLight: 'bg-blue-50',
    textColor: 'text-white',
    textColorDark: 'text-blue-700',
    borderColor: 'border-blue-300',
    icon: CheckCircle,
    iconEmoji: '✓',
    description: 'Pedido confirmado e em andamento',
    ordem: 3
  },
  'ARQUIVADO': {
    label: 'Arquivado',
    labelShort: 'arquivado',
    bgGradient: 'from-slate-400 to-slate-500',
    bgSolid: 'bg-slate-400',
    bgLight: 'bg-slate-50',
    textColor: 'text-white',
    textColorDark: 'text-slate-700',
    borderColor: 'border-slate-300',
    icon: FileX,
    iconEmoji: '📁',
    description: 'Proposta arquivada',
    ordem: 6
  },
  'CANCELADO': {
    label: 'Cancelado',
    labelShort: 'cancelado',
    bgGradient: 'from-red-500 to-red-600',
    bgSolid: 'bg-red-500',
    bgLight: 'bg-red-50',
    textColor: 'text-white',
    textColorDark: 'text-red-700',
    borderColor: 'border-red-300',
    icon: XCircle,
    iconEmoji: '✗',
    description: 'Proposta cancelada',
    ordem: 6
  }
};

// Função para obter config de fase
export const getFaseConfig = (fase) => {
  return FASE_CONFIG[fase] || FASE_CONFIG['AGUARDANDO_PROXIMA_ETAPA'];
};

// ========================================
// CONFIGURAÇÃO DOS STATUS DE PONTUAÇÃO
// =========================== =============
export const STATUS_CONFIG = {
  'INVIÁVEL': {
    label: 'INVIÁVEL',
    bgGradient: 'from-red-400 to-red-500',        // Vermelho fraco
    bgSolid: 'bg-red-400',
    bgLight: 'bg-red-50',
    textColor: 'text-white',
    textColorDark: 'text-red-700',
    borderColor: 'border-red-300',
    icon: XCircle,
    iconEmoji: '✗',
    description: 'Pedido não viável comercialmente'
  },
  'EM ANÁLISE': {
    label: 'EM ANÁLISE',
    bgGradient: 'from-amber-400 to-amber-500',    // Amarelo
    bgSolid: 'bg-amber-400',
    bgLight: 'bg-amber-50',
    textColor: 'text-white',
    textColorDark: 'text-amber-700',
    borderColor: 'border-amber-300',
    icon: AlertTriangle,
    iconEmoji: '⚠',
    description: 'Aguardando aprovação da Diretoria'
  },
  'VIÁVEL': {
    label: 'VIÁVEL',
    bgGradient: 'from-lime-400 to-lime-500',      // Verde fraco
    bgSolid: 'bg-lime-400',
    bgLight: 'bg-lime-50',
    textColor: 'text-white',
    textColorDark: 'text-lime-700',
    borderColor: 'border-lime-300',
    icon: ArrowRight,
    iconEmoji: '→',
    description: 'Pedido viável para prosseguir'
  },
  'BOA VENDA': {
    label: 'BOA VENDA',
    bgGradient: 'from-green-500 to-green-600',    // Verde mais forte
    bgSolid: 'bg-green-500',
    bgLight: 'bg-green-50',
    textColor: 'text-white',
    textColorDark: 'text-green-700',
    borderColor: 'border-green-300',
    icon: CheckCircle,
    iconEmoji: '✓',
    description: 'Boa margem de lucro'
  },
  'EXCELENTE': {
    label: 'EXCELENTE',
    bgGradient: 'from-emerald-600 to-emerald-700', // Verde bem forte
    bgSolid: 'bg-emerald-600',
    bgLight: 'bg-emerald-50',
    textColor: 'text-white',
    textColorDark: 'text-emerald-700',
    borderColor: 'border-emerald-300',
    icon: Trophy,                                   // Ícone de campeão/troféu
    iconEmoji: '🏆',
    description: 'Excelente margem de lucro'
  },
  'APROVADO': {
    label: 'PEDIDO APROVADO',
    labelShort: 'APROVADO',
    bgGradient: 'from-teal-500 to-teal-600',       // Verde diferente (teal)
    bgSolid: 'bg-teal-500',
    bgLight: 'bg-teal-50',
    textColor: 'text-white',
    textColorDark: 'text-teal-700',
    borderColor: 'border-teal-300',
    icon: Check,                                    // Ícone de check/certo
    iconEmoji: '✓',
    description: 'Pedido aprovado pela Diretoria'
  },
  'RECUSADO': {
    label: 'PEDIDO RECUSADO',
    labelShort: 'RECUSADO',
    bgGradient: 'from-red-600 to-red-700',         // Vermelho forte
    bgSolid: 'bg-red-600',
    bgLight: 'bg-red-50',
    textColor: 'text-white',
    textColorDark: 'text-red-700',
    borderColor: 'border-red-300',
    icon: X,                                        // Ícone de X
    iconEmoji: '✗',
    description: 'Pedido recusado pela Diretoria'
  },
  'REANÁLISE': {
    label: 'REANÁLISE',
    labelShort: 'REANÁLISE',
    bgGradient: 'from-amber-500 to-orange-500',    // Laranja (destaque)
    bgSolid: 'bg-amber-500',
    bgLight: 'bg-amber-50',
    textColor: 'text-white',
    textColorDark: 'text-amber-700',
    borderColor: 'border-amber-300',
    icon: RotateCcw,                                // Ícone de reanálise
    iconEmoji: '↻',
    description: 'Pedido enviado para reanálise pelo vendedor'
  }
};

// ========================================
// FUNÇÃO PARA OBTER CONFIG DE STATUS
// ========================================
export const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || STATUS_CONFIG['EM ANÁLISE'];
};

// ========================================
// FUNÇÃO PARA CALCULAR STATUS POR PONTUAÇÃO GERAL
// ========================================
// Regra baseada na pontuação do pedido:
// Excelente: >= 10,00
// Boa Venda: >= 5,00 e < 10,00
// Viável: > 0,00 e < 5,00
// Em Análise: >= -10,00 e <= 0,00
// Inviável: < -10,00
export const getStatusByScore = (pontuacao) => {
  // Arredondar para 2 casas decimais para evitar problemas de ponto flutuante
  // Ex: 0.0000000001 vira 0.00, evitando classificar como VIÁVEL quando deveria ser EM ANÁLISE
  const p = Math.round((pontuacao || 0) * 100) / 100;
  if (p >= 10) return 'EXCELENTE';
  if (p >= 5) return 'BOA VENDA';
  if (p > 0) return 'VIÁVEL';
  if (p >= -10) return 'EM ANÁLISE';
  return 'INVIÁVEL';
};

// ========================================
// FUNÇÃO PARA CALCULAR STATUS POR PONTUAÇÃO (ALIAS - mantido para compatibilidade)
// ========================================
export const getStatusByMargem = getStatusByScore;

// ========================================
// FUNÇÃO PARA OBTER CONFIG POR PONTUAÇÃO
// ========================================
export const getScoreConfig = (pontuacao) => {
  const status = getStatusByScore(pontuacao);
  return {
    ...STATUS_CONFIG[status],
    status
  };
};

// ========================================
// COMPONENTE DE BADGE DE STATUS
// Uso: <StatusBadge status="EXCELENTE" />
// ========================================
export const StatusBadge = ({ status, size = 'md', tamanho, showIcon = true, fullLabel = false, fullWidth = false }) => {
  const config = getStatusConfig(status);
  const IconComponent = config.icon;
  
  // Aceita tanto 'size' quanto 'tamanho'
  const finalSize = tamanho || size;
  
  const sizeClasses = {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-4 text-lg'
  };
  
  const iconSizes = {
    xs: 12,
    sm: 14,
    md: 18,
    lg: 22,
    xl: 26
  };
  
  const label = fullLabel && config.label ? config.label : (config.labelShort || config.label);
  
  return (
    <div className={`inline-flex items-center justify-center gap-1.5 ${fullWidth ? 'w-full' : ''} ${sizeClasses[finalSize]} rounded-lg font-bold text-white bg-gradient-to-r ${config.bgGradient}`}>
      {showIcon && <IconComponent size={iconSizes[finalSize]} className="text-white" />}
      <span>{label}</span>
    </div>
  );
};

// ========================================
// COMPONENTE DE CARD DE STATUS (para resumos)
// ========================================
export const StatusCard = ({ status, pontuacao, showScore = true }) => {
  const config = getStatusConfig(status);
  const IconComponent = config.icon;
  
  return (
    <div className={`bg-gradient-to-r ${config.bgGradient} text-white font-bold py-3 px-6 rounded-xl`}>
      <div className="flex items-center justify-center gap-3">
        <IconComponent size={24} className="text-white" />
        <span className="text-lg">{config.label}</span>
      </div>
      {showScore && pontuacao !== undefined && (
        <div className="text-center mt-2 text-white/80 text-sm">
          Pontuação: {pontuacao.toFixed(2)}
        </div>
      )}
    </div>
  );
};

// ========================================
// COMPONENTE DE INFO DE APROVAÇÃO/RECUSA
// ========================================
export const ApprovalInfo = ({ status, aprovadoPor, observacao }) => {
  const config = getStatusConfig(status);
  const IconComponent = config.icon;
  const isAprovado = status === 'APROVADO';
  
  return (
    <div className={`mt-3 ${config.bgLight} border ${config.borderColor} rounded-lg p-3`}>
      <div className="flex items-center gap-2 mb-1">
        <IconComponent size={16} className={config.textColorDark} />
        <p className={`text-xs font-semibold ${config.textColorDark} uppercase`}>
          {isAprovado ? 'Aprovado pela Diretoria' : 'Motivo da Recusa:'}
        </p>
      </div>
      {observacao && (
        <p className={`text-sm ${config.textColorDark} ${isAprovado ? '' : 'mb-1'}`}>
          {observacao}
        </p>
      )}
      {aprovadoPor && (
        <p className={`text-xs ${config.textColorDark} opacity-70 mt-1`}>
          Por: {aprovadoPor}
        </p>
      )}
    </div>
  );
};

// ========================================
// COMPONENTE DE BADGE DE FASE DO FLUXO
// Uso: <FaseBadge fase="AGUARDANDO_APROVACAO" />
// ========================================
export const FaseBadge = ({ fase, size = 'md', tamanho, showIcon = true, fullLabel = false, fullWidth = false }) => {
  const config = getFaseConfig(fase);
  const IconComponent = config.icon;
  
  const finalSize = tamanho || size;
  
  const sizeClasses = {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-4 text-lg'
  };
  
  const iconSizes = {
    xs: 12,
    sm: 14,
    md: 18,
    lg: 22,
    xl: 26
  };
  
  const label = fullLabel ? config.label : (config.labelShort || config.label);
  
  return (
    <div className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap ${fullWidth ? 'w-full' : ''} ${sizeClasses[finalSize]} rounded-lg font-bold text-white bg-gradient-to-r ${config.bgGradient}`}>
      {showIcon && IconComponent && <IconComponent size={iconSizes[finalSize]} className="text-white" />}
      <span>{label}</span>
    </div>
  );
};

// ========================================
// COMPONENTE DE CARD DE FASE (para mostrar fase atual)
// ========================================
export const FaseCard = ({ fase, mostrarDescricao = true }) => {
  const config = getFaseConfig(fase);
  const IconComponent = config.icon;
  
  return (
    <div className={`bg-gradient-to-r ${config.bgGradient} text-white font-bold py-3 px-6 rounded-xl`}>
      <div className="flex items-center justify-center gap-3">
        {IconComponent && <IconComponent size={24} className="text-white" />}
        <span className="text-lg">{config.label}</span>
      </div>
      {mostrarDescricao && config.description && (
        <div className="text-center mt-2 text-white/80 text-sm">
          {config.description}
        </div>
      )}
    </div>
  );
};

export default STATUS_CONFIG;